import React from 'react';
import { useEditor } from '@lidojs/design-editor';
import SidebarTab from '../tabs/TabList';
import FrameContent from './sidebar/FrameContent';
import GraphicContent from './sidebar/GraphicContent';
import ImageContent from './sidebar/ImageContent';
import ShapeContent from './sidebar/ShapeContent';
import TemplateContent from './sidebar/TemplateContent';
import TextContent from './sidebar/TextContent';
import UploadContent from './sidebar/UploadContent';
import { useTranslation } from 'react-i18next';
import {
  RiCollageLine,
  RiTBoxLine,
  RiImageCircleLine,
  RiShapesLine,
  RiEmotionLaughLine,
  RiUpload2Line,
} from '@remixicon/react';

interface SidebarProps {
  activeTab: string | null;
  onTabChange: (tab: string | null) => void;
  images: { url: string; type: 'svg' | 'image' }[];
  onAddImage: (images: { url: string; type: 'svg' | 'image' }) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onTabChange,
  images,
  onAddImage,
}) => {
  const { t } = useTranslation();

  const tabs = [
    {
      name: 'Template',
      label: t('page_editor.general.template'),
      icon: <RiCollageLine />,
    },
    {
      name: 'Text',
      label: t('page_editor.general.text'),
      icon: <RiTBoxLine />,
    },
    {
      name: 'Shape',
      label: t('page_editor.general.shape'),
      icon: <RiShapesLine />,
    },
    {
      name: 'Image',
      label: t('page_editor.general.image'),
      icon: <RiImageCircleLine />,
    },
    {
      name: 'Graphic',
      label: t('page_editor.general.graphic'),
      icon: <RiEmotionLaughLine />,
    },
    {
      name: 'Upload',
      label: t('page_editor.general.upload'),
      icon: <RiUpload2Line />,
    },
  ];

  const { actions } = useEditor();

  return (
    <div
      css={(theme) => ({
        display: 'flex',
        zIndex: 2,
        position: 'relative',
        background: theme.colors.background,
        borderRight: `1px solid ${theme.colors.stroke}`,
      })}
    >
      <div css={(theme) => ({ display: 'flex' })}>
        <SidebarTab
          active={activeTab}
          tabs={tabs}
          onChange={(_, tab) => {
            actions.setSidebar();
            onTabChange(tab);
          }}
        />
        {activeTab && (
          <div
            css={(theme) => ({
              width: 360,
              borderLeft: `1px solid ${theme.colors.stroke}`,
              '@media (max-width: 1024px)': {
                width: '100%',
                position: 'fixed',
                bottom: 0,
                left: 0,
                top: 0,
                background: theme.colors.background,
              },
            })}
          >
            {activeTab === 'Template' && (
              <TemplateContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            {activeTab === 'Text' && (
              <TextContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            {activeTab === 'Frame' && (
              <FrameContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            {activeTab === 'Image' && (
              <ImageContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            {activeTab === 'Graphic' && (
              <GraphicContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            {activeTab === 'Shape' && (
              <ShapeContent
                onClose={() => {
                  onTabChange(null);
                  actions.setSidebar();
                }}
              />
            )}
            <UploadContent
              images={images}
              onAddImage={onAddImage}
              visibility={activeTab === 'Upload'}
              onClose={() => {
                onTabChange(null);
                actions.setSidebar();
              }}
            />
          </div>
        )}
      </div>
      <div
        css={(theme) => ({
          width: 360,
          position: 'absolute',
          overflow: 'hidden',
          top: 0,
          left: 73,
          height: '100%',
          pointerEvents: 'none',
        })}
        id={'settings'}
      />
    </div>
  );
};

export default Sidebar;
